import * as actions from './actions';
import PageHolder from '../../../../components/containers/PageHolder/PageHolder';

export const initialRouteState = {
  // Route settings
  locationOnRouteLoad: {
    lat: null,
    lon: null,
  },
  currentMapParams: {
    viewPortDetails: {
      center: {
        lat: null,
        lon: null,
      },
      zoom: 12,
      bearing: 0,
      pitch: 0,
      bounds: null,
    },
  },
  appViewportSize: {
    width: null,
    height: null,
  },
};
export const initialMetricState = {
  measurementSystem: 'metric',
  speed: 'kilometrePerHour',
  distance: 'kilometre',
  temperature: 'celsius',
  windSpeed: 'kmh',
  swellHeight: 'metres',
  tideHeight: 'metres',
  coordinate: 'degreesMinutesSeconds',
};

export const initialImperialState = {
  measurementSystem: 'imperial',
  speed: 'knots',
  distance: 'nauticalMile',
  temperature: 'fahrenheit',
  windSpeed: 'knots',
  swellHeight: 'feet',
  tideHeight: 'feet',
  coordinate: 'degreesMinutesSeconds',
};

export const initialSettingsState = {
  memberSettings: {
    unitsConfig:
      window?.DECKEE_CONFIG?.defaultSettingsToMetric ?? true // default to true so that it sets as metric
        ? initialMetricState
        : initialImperialState,
    tripsConfig: {
      autolock: true,
    },
    weatherConfig: {
      weatherOnStartup: false,
      weatherOnMapCenter: false,
    },
    locationConfig: {
      signalMapSetting: 'anonymous',
      onTheWaterDetectionEnabled: false,
    },
    weatherLayoutConfig: [
      {
        id: 'riskForecast',
        label: 'Risk forecast',
        order: 0.9,
        showGraph: true,
        active: true,
      },
      {
        id: 'currentConditions',
        label: 'Current conditions',
        order: 1,
        showGraph: false,
        active: true,
      },
      {
        id: 'outlook',
        label: 'Outlook',
        order: 2,
        showGraph: false,
        active: true,
      },
      {
        id: 'moonPhases',
        label: 'Moon phases',
        order: 8,
        showGraph: false,
        active: true,
      },
      {
        id: 'temperature',
        label: 'Temperature',
        order: 3,
        showGraph: true,
        active: true,
      },
      {
        id: 'wind',
        label: 'Wind',
        order: 4,
        showGraph: true,
        active: true,
      },
      {
        id: 'swell',
        label: 'Swell',
        order: 5,
        showGraph: true,
        active: true,
      },
      {
        id: 'tides',
        label: 'Tides',
        order: 6,
        showGraph: true,
        active: true,
      },
      {
        id: 'rainfall',
        label: 'Rainfall',
        order: 7,
        showGraph: true,
        active: true,
      },
    ],
    waterBodyDefinitionConfig: {
      latitude: undefined,
      longitude: undefined,
      intendedWatersName: undefined,
      intendedAreaWaterBodyKey: undefined,
    },
  },
  selectedBaseMapLayer: 'satellite',
  selectedLayerTypes: [],

  // Share notifications
  newShareNotifications: false,
};

const initialBaseMapState = {
  // AppControls config
  showingMenuBar: true,

  showingSearchBar: true,
  showingControlBar: false,
  controlBarType: 'default',

  showingControlPaneMenu: true,
  showingControlPaneSettings: true,
  showingCurrentLocationButton: true,
  showingZoomControls: true,
  showingResetBearingAndPitch: true,

  // Bottom control pane on map
  showingBottomControlPane: true,
  showingSearchButton: true,
  showingSignalButton: true,
  showingFriendsButton: true,

  showingAlertsButton: true,
  showingMapBaseLayerSelector: true,
  showingAddMarkerMenu: false,
  showingSlideUpMenu: false,
  showingOverlay: false,
  transparentStatusBar: false,
  hideStatusBar: false,
  allowOverlayOnClick: true,
  showingPopOver: false,
  popOverConfig: null,
  popOverHeading: null,
  overlayZindex: 3000,
  showingSelectMapPoint: 0,

  showToastMessage: false,

  // PageHolder config
  showingPageHolder: false,
  pageHolderType: PageHolder.types.SIDE_BAR,
  pageHolderTransparent: false,
  pageHolderElevation: false,
  // pageHolderShiftLeft: 0,

  // Map config
  mapHolderOffset: '0px',
  floatingPaneOffset: null,
  mapLocationChange: undefined,
  hidingAllLayers: false,
  showingDistanceTool: false,
  showingLocationMapMarker: false,
  locationMarkerPosition: {},

  newMarkerType: null,
  selectedMarkerIds: [],
  selectedMarkerType: null,

  // Trips config
  showingActiveTripButton: true,
  showingFollowButton: false,
};

const initialAppState = {
  ...initialRouteState,
  ...initialSettingsState,
  ...initialBaseMapState,
};

export const reducer = (state = initialAppState, action) => {
  let newState = {};

  switch (action.type) {
    case actions.APP_CONTROLS_SET_TRANSPARENT_STATUS_BAR:
      return {
        ...state,
        transparentStatusBar: action.payload,
      };

    case actions.APP_CONTROLS_SET_HIDE_STATUS_BAR:
      return {
        ...state,
        hideStatusBar: action.payload,
      };

    case actions.APP_CONTROLS_MANAGER_SET_BASE_MAP:
      return {
        ...state,
        ...initialBaseMapState,
      };

    case actions.APP_CONTROLS_MANAGER_SET_LOCATION_DISCLOSURE_MODAL:
      return {
        ...state,
        ...initialBaseMapState,
        showingOverlay: true,
        overlayZindex: 120000,
      };
    case actions.APP_CONTROLS_MANAGER_SET_EMAIL_AUTHENTICATION:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH,
        pageHolderElevation: false,
      };

    case actions.APP_CONTROLS_MANAGER_SET_MAIN_MENU:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        pageHolderElevation: true,

        showingOverlay: true,
        overlayZindex: 5000,
      };

    case actions.APP_CONTROLS_MANAGER_SET_MEASURE_DISTANCE:
      return {
        ...state,
        ...initialBaseMapState,
        showingControlBar: true,
        controlBarType: 'distance',
        showingDistanceTool: true,

        // Hide all the extra map controls
        hidingAllLayers: true,
        showingSearchBar: false,
        showingActiveTripButton: false,
        showingFriendsButton: false,
        showingSignalButton: false,
        showingAlertsButton: false,
        showingControlPaneMenu: false,
        showingControlPaneSettings: false,
        showingCurrentLocationButton: false,
        showingZoomControls: false,
        showingResetBearingAndPitch: false,
      };

    case actions.APP_CONTROLS_MANAGER_SET_SIDE_BAR: // Default side bar page holder
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };

    case actions.APP_CONTROLS_MANAGER_SET_SIDE_BAR_NO_MAIN_MENU: // Side bar page holder with no menu-bar
      // eslint-disable-next-line no-console
      console.warn('This action has been deprecated');
      return {
        ...state,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };

    case actions.APP_CONTROLS_MANAGER_SET_FLOATING_PANE: // Floating pane page holder with no menu-bar
      return {
        ...state,
        ...initialBaseMapState,
        hidingAllLayers: state.hidingAllLayers,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FLOATING_PANEL,
        pageHolderTransparent: true,

        // Hide all the extra map controls
        showingSearchBar: false,
        showingActiveTripButton: false,
        showingControlPaneMenu: false,
        showingBottomControlPane: false,
        showingControlPaneSettings: false,
        showingCurrentLocationButton: false,
        showingZoomControls: false,
        showingResetBearingAndPitch: false,
        showingSignalButton: false,
        showingAlertsButton: false,
      };

    // Floating pane page holder with no menu-bar and overlay
    case actions.APP_CONTROLS_MANAGER_SET_FLOATING_PANE_WITH_OVERLAY:
      // eslint-disable-next-line no-console
      console.warn('This action has been deprecated');
      return {
        ...state,
        ...initialBaseMapState,
        hidingAllLayers: state.hidingAllLayers,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FLOATING_PANEL,
        pageHolderTransparent: true,
        showingOverlay: true,
        allowOverlayOnClick: false,
        overlayZindex: 5000,

        // Hide all the extra map controls
        showingSearchBar: false,
        showingActiveTripButton: false,
        showingControlPaneMenu: false,
        showingControlPaneSettings: false,
        showingCurrentLocationButton: false,
        showingZoomControls: false,
        showingResetBearingAndPitch: false,
        showingSignalButton: false,
        showingAlertsButton: false,
      };

    // Side bar page holder with non dismissible overlay
    case actions.APP_CONTROLS_MANAGER_SET_SIDE_BAR_WITH_HARD_OVERLAY:
      // eslint-disable-next-line no-console
      console.warn('This action has been deprecated');
      return {
        ...state,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        showingOverlay: true,
        allowOverlayOnClick: false,
        overlayZindex: 5000,
      };

    case actions.APP_CONTROLS_MANAGER_SHOW_SLIDE_UP_MENU:
      return {
        ...state,
        ...initialBaseMapState,
        showingOverlay: true,
        showingSlideUpMenu: true,
      };

    case actions.APP_CONTROLS_MANAGER_HIDE_MAP_CONTROLS:
      newState = { ...state };

      if (action.payload) {
        newState.hidingAllLayers = true;
      }

      return {
        ...newState,
        ...initialBaseMapState,
        // Hide all the extra map controls
        showingSearchBar: false,
        showingActiveTripButton: false,
        showingControlPaneMenu: false,
        showingControlPaneSettings: false,
        showingCurrentLocationButton: false,
        showingZoomControls: false,
        showingResetBearingAndPitch: false,
        showingSignalButton: false,
        showingAlertsButton: false,
        showingBottomControlPane: false,
      };

    case actions.APP_CONTROLS_MANAGER_HIDE_SEARCH_BUTTON:
      newState = { ...state };
      return {
        ...newState,
        showingSearchButton: !!action.payload,
      };

    case actions.APP_CONTROLS_MANAGER_HIDE_BOTTOM_CONTROL_PANE:
      newState = { ...state };
      return {
        ...newState,
        showingBottomControlPane: !!action.payload,
      };

    case actions.APP_CONTROLS_MANAGER_SET_NEW_MARKER_ENTITY_TYPE:
      newState = { ...state };

      if (action.payload) {
        newState.hidingAllLayers = true;
      }

      return {
        ...newState,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
        showingOverlay: true,
        allowOverlayOnClick: false,
        overlayZindex: 5000,
      };

    case actions.APP_CONTROLS_MANAGER_SET_NEW_MARKER_CONTENT:
      newState = { ...state };

      if (action.payload) {
        newState.hidingAllLayers = true;
      }

      return {
        ...newState,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
        showingOverlay: true,
        allowOverlayOnClick: false,
        overlayZindex: 5000,
        // newMarkerType: { ...action.payload },
        // eslint-disable-next-line no-warning-comments
        // TODO: add latLon
      };

    case actions.APP_CONTROLS_MANAGER_CLOSE_POPOVER:
      newState = { ...state };

      return {
        ...newState,
        showingPopOver: false,
        popOverConfig: null,
        popOverHeading: null,
      };

    case actions.APP_CONTROLS_MANAGER_SHOW_POPOVER:
      newState = { ...state };

      return {
        ...newState,
        // ...initialBaseMapState,
        showingPopOver: true,
        showingPageHolder: !!action.payload,
        mapHolderOffset: '0px',
        popOverConfig: action.payload,
        popOverHeading: action.heading,
      };

    case actions.APP_CONTROLS_MANAGER_SHOW_POPOVER_MESSAGE:
      newState = { ...state };

      return {
        ...newState,
        ...initialBaseMapState,

        showingPopOver: true,
        showingPageHolder: true,
        mapHolderOffset: '0px',
        popOverConfig: action.payload,
        popOverHeading: action.heading,
      };

    case 'SHEET_OPEN':
      return {
        ...state,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FLOATING_PANEL,
        pageHolderTransparent: true,
        showingResetBearingAndPitch: false,
        mapHolderOffset: '0px',
      };

    case 'FORCE_POPOVER':
      return {
        ...state,
        showingPopOver: true,
        pageHolderType: PageHolder.types.FLOATING_PANEL,
        pageHolderTransparent: true,
        showingPageHolder: true,
        showingMenuBar: false,
        showingResetBearingAndPitch: false,
      };

    case 'POPOVER_CLOSE':
      return {
        ...state,
        showingMenuBar: true,
      };

    case actions.APP_CONTROLS_MANAGER_SET_MARKER_SUMMARY:
      newState = { ...state };

      return {
        ...newState,
        ...initialBaseMapState,
        newMarkerType: { ...action.payload },
        selectedMarkerType: action.payload.mapMarkerType,
        selectedMarkerIds: action.payload.selectedMarkerId
          ? [action.payload.selectedMarkerId]
          : [],

        showingPopOver: true,
      };

    case actions.APP_CONTROLS_MANAGER_SET_MARKER_SUMMARY_DETAILS:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,

        selectedMarkerType: action.payload.mapMarkerType,
        selectedMarkerIds: action.payload.selectedMarkerId
          ? [action.payload.selectedMarkerId]
          : [],
      };

    case actions.APP_CONTROLS_MANAGER_SET_MEMBER_DETAILS:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,

        memberId: action.payload.memberId,
      };

    // Need to look at merging some of these now into a generic page holder case
    case actions.APP_CONTROLS_MANAGER_SET_MARKER_FLAG:
      return {
        ...state,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,

        selectedMarkerIds: action.payload.selectedMarkerId
          ? [action.payload.selectedMarkerId]
          : [],
      };
    case actions.APP_CONTROLS_MANAGER_SET_MARKER_EDIT:
      return {
        ...state,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,

        selectedMarkerIds: action.payload.selectedMarkerId
          ? [action.payload.selectedMarkerId]
          : [],
        selectedMarkerType: action.payload.mapMarkerType,
      };
    case actions.APP_CONTROLS_MANAGER_BOAT_DETAILS_EDIT:
      return {
        ...state,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,

        watercraftId: action.payload.watercraftId,
      };
    case actions.APP_CONTROLS_MANAGER_SET_NEW_MARKER_COMMENT:
      return {
        ...state,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,

        selectedMarkerIds: action.payload.selectedMarkerId
          ? [action.payload.selectedMarkerId]
          : [],
        selectedMarkerType: action.payload.mapMarkerType,
      };

    case actions.APP_CONTROLS_MANAGER_SET_WEATHER:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_TRIP_SETTINGS:
      return {
        ...state,
        ...initialBaseMapState,
        showingMenuBar: true,
        showingControlPaneMenu: true,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_WEATHER_LOCATION_SEARCH:
      return {
        ...state,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
        showingOverlay: true,
        overlayZindex: 5000,
      };

    case actions.APP_CONTROLS_MANAGER_SET_MAP_OPTIONS:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };

    case actions.APP_CONTROLS_MANAGER_SET_MAP_OPTIONS_NAV:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };

    // ################### Safety Information #######################
    case actions.APP_CONTROLS_MANAGER_SET_SAFETY_INFORMATION:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SAFETY_INFO_NSW:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SAFETY_INFO_WA:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SAFETY_INFO_WA_DEPARTMENTS:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SAFETY_INFO_TAS:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SAFETY_INFO_NSW_CHECKLIST:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SAFETY_INFO_NSBC:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SETTINGS:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SETTINGS_FEATURES:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SETTINGS_HELM:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_SETTINGS_HELM_OVERRIDE:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_GET_NOTIFIED:
      return {
        ...state,
        ...initialBaseMapState,
        showingMenuBar: false,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_ABOUT:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_PROFILE:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };
    case actions.APP_CONTROLS_MANAGER_SET_LOCATION_SEARCH:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH_MOBILE,
        showingMenuBar: false,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
        showingOverlay: true,
        overlayZindex: 5000,
      };

    case actions.APP_CONTROLS_MANAGER_SET_MY_TRIPS:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        pageHolderElevation: true,
        mapHolderOffset: '200px',
        // pageHolderType: PageHolder.types.FLOATING_PANEL,
        // pageHolderTransparent: true,
      };

    case actions.APP_CONTROLS_MANAGER_SET_NOTIFS:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.SIDE_BAR,
        mapHolderOffset: '200px',
        pageHolderElevation: true,
      };

    case actions.APP_CONTROLS_MANAGER_SET_FLOATING_PANE_WITH_MENU_AND_SEARCH:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        showingMenuBar: true,
        pageHolderType: PageHolder.types.FLOATING_PANEL,
        pageHolderTransparent: true,
        showingResetBearingAndPitch: false,
      };

    case actions.APP_CONTROLS_MANAGER_SET_404:
      return {
        ...state,
        ...initialBaseMapState,
        showingPageHolder: true,
        pageHolderType: PageHolder.types.FULL_WIDTH,
      };

    // //////////
    case actions.APP_CONTROLS_MANAGER_UPDATE_ROUTE_LOCATION_ON_LOAD: {
      if (!action.payload) return { ...state };

      newState = { ...state };
      newState.locationOnRouteLoad = action.payload;

      return { ...newState };
    }

    case actions.APP_CONTROLS_MANAGER_HIDE_ALL_LAYERS: {
      return {
        ...state,
        hidingAllLayers: action.payload || initialAppState.hidingAllLayers,
      };
    }

    case actions.APP_CONTROLS_UPDATE_PUBLIC_LOCATION: {
      return {
        ...state,
        memberSettings: {
          ...state.memberSettings,
          locationConfig: {
            ...state.memberSettings.locationConfig,
            ...action.payload,
          },
        },
      };
    }

    case actions.APP_CONTROLS_MANAGER_UPDATE_MEMBER_SETTINGS: {
      if (!action.payload) return { ...state };

      newState = { ...state };

      newState.memberSettings = {
        ...newState.memberSettings,
        ...action.payload,
      };

      return { ...newState };
    }

    case actions.APP_CONTROLS_MANAGER_LOG_OUT_RESET: {
      // This is fired when the user logs out and in order to show the login form
      // we need the pageHolder to show and menubar hidden
      return {
        ...initialAppState,
        showingPageHolder: true,
        showingMenuBar: false,
        showingOverlay: true,
      };
    }

    case actions.APP_CONTROLS_MANAGER_UPDATE_CURRENT_MAP_PARAMS: {
      if (!action.payload || !action.payload.viewPortDetails)
        return { ...state };

      newState = { ...state };
      newState.currentMapParams = action.payload;

      return { ...newState };
    }

    case actions.APP_CONTROLS_MANAGER_UPDATE_APP_VIEWPORT_SIZE: {
      if (!action.payload) return { ...state };

      newState = { ...state };
      newState.appViewportSize = action.payload;

      return { ...newState };
    }

    case actions.APP_CONTROLS_MANAGER_UPDATE_SELECTED_BASE_MAP_LAYER: {
      newState = { ...state };

      if (!action.payload) return { ...state };

      return {
        ...newState,
        selectedBaseMapLayer: action.payload,
      };
    }

    case actions.APP_CONTROLS_MANAGER_UPDATE_SELECTED_MARKER: {
      newState = { ...state };

      if (!action.payload) return { ...state };

      return {
        ...newState,
        selectedMarkerType: action.payload.mapMarkerType || null,
        selectedMarkerIds: action.payload.selectedMarkerId
          ? [action.payload.selectedMarkerId]
          : [],
      };
    }

    case actions.APP_CONTROLS_MANAGER_UPDATE_TOAST_MESSAGE: {
      newState = { ...state };

      return {
        ...newState,
        showToastMessage: !!action.payload,
      };
    }

    case actions.APP_CONTROLS_MANAGER_SHOW_SELECT_MAP_POINT: {
      newState = { ...state };

      return {
        ...newState,
        showingSelectMapPoint: action.payload,
      };
    }

    case actions.APP_CONTROLS_MANAGER_SHOW_LOCATION_MAP_MARKER: {
      newState = { ...state };

      if (!action.payload) return { ...state };

      return {
        ...newState,
        showingLocationMapMarker: true,
        locationMarkerPosition: action.payload,
        mapLocationChange: { center: action.payload },
      };
    }

    case actions.APP_CONTROLS_MANAGER_MOVE_MAP_TO_LOCATION: {
      newState = { ...state };

      if (!action.payload) return { ...state };

      return {
        ...newState,
        mapLocationChange: {
          ...state.mapLocationChange,
          ...action.payload,
        },
      };
    }

    case actions.APP_CONTROLS_MANAGER_CLEAR_MAP_LOCATION_CHANGE: {
      newState = { ...state };
      newState.mapLocationChange = undefined;

      return { ...newState };
    }

    case actions.APP_CONTROLS_MANAGER_SHOW_CONTROL_PANE: {
      return {
        ...state,
        showingControlPaneMenu: true,
        showingControlPaneSettings: true,
        showingCurrentLocationButton: true,
        showingZoomControls: true,
        showingResetBearingAndPitch: true,
        showingAlertsButton: true,
      };
    }

    case actions.APP_CONTROLS_MANAGER_HIDE_CONTROL_PANE: {
      return {
        ...state,
        showingControlPaneMenu: false,
        showingControlPaneSettings: false,
        showingCurrentLocationButton: false,
        showingZoomControls: false,
        showingResetBearingAndPitch: false,
        showingAlertsButton: false,
      };
    }

    case actions.APP_CONTROLS_MANAGER_SHOW_CONTROL_PANE_SETTINGS: {
      return {
        ...state,
        showingControlPaneSettings: true,
      };
    }

    case actions.APP_CONTROLS_MANAGER_SHOW_CONTROL_PANE_CURRENT_LOCATION: {
      return {
        ...state,
        showingCurrentLocationButton: true,
      };
    }

    case actions.APP_CONTROLS_MANAGER_SHOW_CONTROL_PANE_ZOOM: {
      return {
        ...state,
        showingZoomControls: true,
      };
    }

    case actions.APP_CONTROLS_MANAGER_SHOW_CONTROL_PANE_RESET: {
      return {
        ...state,
        showingResetBearingAndPitch: true,
      };
    }

    case actions.APP_CONTROLS_MANAGER_HIDE_CONTROL_PANE_RESET: {
      return {
        ...state,
        showingResetBearingAndPitch: false,
      };
    }

    case actions.APP_CONTROLS_UPDATE_SHARE_NOTIFICATIONS: {
      return {
        ...state,
        newShareNotifications: Boolean(action.payload),
      };
    }

    case actions.APP_CONTROLS_MANAGER_SHOW_FOLLOW_BUTTON: {
      return {
        ...state,
        showingFollowButton: true,
      };
    }

    case actions.APP_CONTROLS_MANAGER_HIDE_FOLLOW_BUTTON: {
      return {
        ...state,
        showingFollowButton: false,
      };
    }

    case actions.APP_CONTROLS_MANAGER_SET_FLOATING_PANE_OFFSET: {
      return {
        ...state,
        floatingPaneOffset:
          action.payload || initialAppState.floatingPaneOffset,
      };
    }

    case actions.APP_CONTROLS_MANAGER_SET_SHOWING_MENU_BAR: {
      return {
        ...state,
        showingMenuBar:
          action.payload !== undefined
            ? action.payload
            : initialAppState.showingMenuBar,
      };
    }

    case actions.APP_CONTROLS_MANAGER_SHOW_PAGE_HOLDER:
      return {
        ...state,
        showingPageHolder: action.payload || initialAppState.showingPageHolder,
      };

    case actions.APP_CONTROLS_MANAGER_SHOW_CONTROL_BAR:
      return {
        ...state,
        controlBarType: action.payload || initialAppState.controlBarType,
        showingControlBar: true,
      };

    case actions.APP_CONTROLS_MANAGER_SHOW_MAP_OVERLAY:
      return {
        ...state,
        showingOverlay: action.payload || initialAppState.showingOverlay,
        allowOverlayOnClick: false,
        overlayZindex: 5000,
      };

    case actions.APP_CONTROLS_MANAGER_SET_DROP_PIN_LOCATION: {
      newState = { ...state };

      if (!action.payload) return { ...state };

      return {
        ...newState,
        dropPinLocation: {
          ...state.dropPinLocation,
          ...action.payload,
        },
      };
    }

    default:
      return state;
  }
};
